import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { UserDetails } from '../../../../shared/models/user';
import { AuthPermission, Permission } from '../../models/permission';
import { ManageUsersService } from '../../services/manage-users.service';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SessionDetailsComponent } from '../../components/session-details/session-details.component';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';

@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [FormsModule, SessionDetailsComponent],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
})
export class UserDetailsComponent extends DestroyBase implements OnChanges {
  @Input({ required: true }) user!: UserDetails;
  @Input({ required: true }) tagColour!: string;

  manageUsersService = inject(ManageUsersService);
  router = inject(Router);
  auditLogService = inject(AuditLogService);

  loading = true;
  permissions!: Permission;
  userPermissions!: AuthPermission[];
  userPermissionsMap: Record<number, boolean> = {};
  isUserPermissionSectionExpanded = false;
  permissionsEnabledCount!: number;
  isExpandedUserSessions = false;

  ngOnChanges(changes: SimpleChanges) {
    if (changes['user'] && changes['user'].currentValue) {
      this.manageUsersService
        .getPermissionsForUser(this.user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          this.permissions = res;
          this.mapPermissionForCheckBox();
          this.loading = false;
        });
    }
  }

  mapPermissionForCheckBox(): void {
    this.permissions.custom_permissions.forEach((ele) => {
      if (
        this.permissions.user_permissions.some(
          (permission) => permission.id === ele.id,
        )
      ) {
        this.userPermissionsMap[ele.id] = true;
      } else {
        this.userPermissionsMap[ele.id] = false;
      }
    });
    this.permissionsEnabledCount = this.permissions.user_permissions.length;
  }

  onUserPermissionChange(permissionId: number, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const permissionName =
      this.permissions.custom_permissions.find((p) => p.id === permissionId)
        ?.codename || '';

    this.auditLogService.logFeature({
      featureName: 'user_permission',
      action: isChecked ? 'enable' : 'disable',
      meta: {
        userId: String(this.user.id),
        userName: this.user.email || '',
        permissionId: String(permissionId),
        permissionName: permissionName,
      },
      description: isChecked
        ? 'User enables user permission'
        : 'User disables user permission',
    });

    if (isChecked) {
      this.manageUsersService
        .addUserPermissionForUser(permissionId, this.user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.permissionsEnabledCount++;
        });
    } else {
      this.manageUsersService
        .deleteUserPermissionForUser(permissionId, this.user.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.permissionsEnabledCount--;
        });
    }
  }

  toggleUserPermissionSection = (): void => {
    this.isUserPermissionSectionExpanded =
      !this.isUserPermissionSectionExpanded;
  };

  onConnectionClick = (connectionName: string): void => {
    this.router.navigate(['/manage-connection-group'], {
      queryParams: { connection: connectionName },
    });
  };

  toggleSection(): void {
    this.isExpandedUserSessions = !this.isExpandedUserSessions;
  }
}
